/**
 * Audio Recording Manager
 * 
 * Handles microphone access and audio recording.
 * Includes 48kHz resampling for OpenAI Realtime API.
 * 
 * Features:
 * - Microphone access and configuration
 * - Audio resampling (native → 48kHz)
 * - Volume monitoring
 * - Pause/resume functionality
 * - Waveform visualization data
 */

import { AudioConfig, AudioLevel } from './types';
import { VoiceActivityDetection } from './VoiceActivityDetection';
import { logger } from '../../utils/logger';
import {
  VAD_ACTIVITY_LOOKBACK_MS,
  SPEECH_STOPPED_WATCHDOG_SILENCE_MS,
} from '../../config/realtime';

export interface RecordingEvents {
  onRecordingStart: () => void;
  onRecordingStop: () => void;
  onAudioLevel: (level: AudioLevel) => void;
  onAudioData: (pcm16Data: Uint8Array) => void; // Raw PCM16 chunks for WebSocket
  onCommitAudio: () => void; // VAD gate closed - commit buffer
  onSpeechDetected: () => void; // 🎯 Client VAD detected speech start
  onSpeechLikely: () => void; // 👁️ Early visual signal — speech looks likely (before gate opens)
  onPTTSpeechDetected?: () => void; // 🎙️ Speech detected during PTT hold (for avatar interruption)
  onError: (error: Error) => void;
  onGateForceClosed?: () => void; // 🛡️ Gate closed due to false positive
  onFalsePositive?: (reason: string) => void; // 🛡️ False positive detected
  onSpeechStoppedTimeout?: () => void; // 🛡️ Início de fala confirmado, fim nunca chegou
}

export class AudioRecording {
  private audioContext: AudioContext;
  private config: AudioConfig;
  private events: Partial<RecordingEvents> = {};
  
  // Recording state
  private isRecording = false;
  private mediaStream: MediaStream | null = null;
  private processedStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private gainNode: GainNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private audioProcessor: ScriptProcessorNode | null = null;
  private resamplerNode: AudioWorkletNode | null = null;
  private useAudioWorklet = true; // Try AudioWorklet first, fallback to ScriptProcessor
  private libsamplerateFallback: any = null; // ✅ libsamplerate instance for ScriptProcessor fallback (using "in modules")
  private smallChunkBuffer: Float32Array | null = null; // ✅ Buffer for small chunks from libsamplerate
  
  // 🎯 Voice Activity Detection with pre-roll buffer
  private vad: VoiceActivityDetection | null = null;
  private vadWasOpen = false; // Track if gate opened (start continuous streaming)
  private lastGateState = false; // Track gate transitions for detecting new speech segments
  private audioPreRollBuffer: Uint8Array[] = []; // Circular buffer of PCM16 chunks
  private readonly MAX_PREROLL_CHUNKS = 60; // ~1000ms at 24kHz with typical chunk sizes (increased from 30)
  
  // 🛡️ Safety timeouts for false positives (non-human sounds)
  private gateOpenedTimestamp: number | null = null;
  private receivedSpeechStarted = false;
  private safetyCheckInterval: NodeJS.Timeout | null = null;
  private readonly SAFETY_CHECK_INTERVAL_MS = 100;
  private voicedSilenceMs = 0;
  private readonly SPEECH_STARTED_TIMEOUT_MS = 3500; // Close gate if no speech_started after 3.5s (increased from 2s)
  
  // 🛡️ Anti-flap cooldown: after a forceCloseGate, prevent immediate gate reopen
  private forceCloseCooldownUntil: number = 0;
  private readonly FORCE_CLOSE_COOLDOWN_MS = 500; // 500ms cooldown after force close
  
  // 🎙️ PTT (Push-to-Talk) mode
  // When pttMode is true, VAD is disabled and audio streaming is controlled
  // by startPTTTransmit() / stopPTTTransmit() instead of the VAD gate.
  private pttMode = false;
  private pttTransmitting = false; // true when PTT button is held down
  private pttPeakRms = 0; // tracks the peak RMS of audio chunks sent during a PTT session
  private pttSpeechCallbackFired = false; // ensures onPTTSpeechDetected fires only once per PTT session
  private readonly PTT_SILENCE_RMS_THRESHOLD = 0.015; // well above digital silence (~0.002), catches soft speech (0.05-0.3)
  
  // Monitoring
  private volumeCheckInterval: NodeJS.Timeout | null = null;

  // Diagnostics: timestamps of last setPlaybackActive transitions
  private lastPlaybackActiveTrueAt = 0;
  private lastPlaybackActiveFalseAt = 0;

  // Per-scenario flag. Stored locally so it can be set before VAD is created
  // (during setupRecording) and re-applied when VAD is rebuilt.
  private atypicalVoiceMode = false;

  constructor(audioContext: AudioContext, config: AudioConfig) {
    this.audioContext = audioContext;
    this.config = config;
  }

  /**
   * Register event listeners
   */
  on<K extends keyof RecordingEvents>(event: K, callback: RecordingEvents[K]): void {
    this.events[event] = callback;
  }

  /**
   * Remove event listeners
   */
  off<K extends keyof RecordingEvents>(event: K): void {
    delete this.events[event];
  }

  /**
   * Check if recording is active
   */
  isRecordingActive(): boolean {
    return this.isRecording;
  }

  /**
   * Get the current AudioContext.
   * This may differ from the one passed to the constructor if setupRecording()
   * detected a sample rate mismatch and recreated the context at the mic's native rate.
   */
  getAudioContext(): AudioContext {
    return this.audioContext;
  }

  /**
   * Internal method: Complete VAD state reset
   * Single source of truth for resetting ALL VAD-related state
   */
  private resetAllVADState(): void {
    logger.debug('[🔄 VAD-STATE] resetAllVADState() called - this should ONLY happen after speech_stopped or conversation end');
    this.audioPreRollBuffer = [];
    this.vadWasOpen = false;
    this.lastGateState = false;
    this.gateOpenedTimestamp = null;
    this.receivedSpeechStarted = false;
    this.stopSafetyCheck();
    
    // Reset internal VAD gate state
    if (this.vad) {
      this.vad.resetState();
    }
  }

  /**
   * Reset VAD state (called when conversation turn ends)
   * Public API for resetting VAD between conversation turns
   */
  resetVADState(): void {
    this.resetAllVADState();
    logger.debug('[🔄 VAD-STATE] VAD reset - ready for next turn');
  }
  
  /**
   * Prepare for next speech segment without stopping audio streaming.
   * 
   * This is called after speech_stopped to allow detecting continued speech.
   * Unlike resetVADState(), this keeps vadWasOpen=true so audio continues
   * streaming to OpenAI, preventing gaps when user continues speaking.
   */
  prepareForNextSpeech(): void {
    // Reset for new speech detection, but keep streaming
    this.gateOpenedTimestamp = null;
    this.receivedSpeechStarted = false;
    this.lastGateState = false; // Allow new transition detection
    this.stopSafetyCheck();
    
    // Reset VAD gate so it can detect new speech
    if (this.vad) {
      this.vad.resetState();
    }
    
    // DO NOT reset vadWasOpen - keep audio streaming!
    // DO NOT clear audioPreRollBuffer - not needed when streaming continues
    logger.debug('[🔄 VAD-STATE] Prepared for next speech - streaming continues');
  }
  
  /**
   * Mark that OpenAI recognized speech (cancels safety timeouts)
   */
  markSpeechStarted(): void {
    this.receivedSpeechStarted = true;
    this.gateOpenedTimestamp = null; // Cancel no_speech_started timeout
    this.voicedSilenceMs = 0;        // Start counting silence for the speech_stopped watchdog
    logger.debug('[🔄 VAD-STATE] ✅ speech_started received - safety timer cancelled, watchdog armed');
  }
  
  /**
   * Start safety check interval to detect false positives
   */
  private startSafetyCheck(): void {
    if (this.safetyCheckInterval) return;
    
    this.safetyCheckInterval = setInterval(() => {
      if (!this.vadWasOpen) return; // Only check if gate is open
      
      const now = Date.now();
      
      // PROTECTION: No speech_started from OpenAI (non-human sound)
      // If OpenAI doesn't recognize as human speech after 2s, it's a false positive
      if (!this.receivedSpeechStarted && this.gateOpenedTimestamp) {
        const timeSinceOpened = now - this.gateOpenedTimestamp;
        if (timeSinceOpened >= this.SPEECH_STARTED_TIMEOUT_MS) {
          console.warn(`[🔄 VAD-STATE] ⚠️ Gate FORCE CLOSED: no speech_started after ${timeSinceOpened}ms`);
          console.warn('[🔄 VAD-STATE] OpenAI did not recognize as human speech');
          this.events.onFalsePositive?.('no_speech_started');
          this.forceCloseGate();
          return;
        }
      }
      
      // 🛡️ PROTECTION: speech_started received but speech_stopped never arrived.
      // Counts continuous acoustic silence; any voiced frame resets it, so long
      // utterances are never cut.
      if (this.receivedSpeechStarted) {
        if (this.vad?.hasRecentAudioActivity(VAD_ACTIVITY_LOOKBACK_MS)) {
          this.voicedSilenceMs = 0;
        } else {
          this.voicedSilenceMs += this.SAFETY_CHECK_INTERVAL_MS;
          if (this.voicedSilenceMs >= SPEECH_STOPPED_WATCHDOG_SILENCE_MS) {
            console.warn(`[🔄 VAD-STATE] ⚠️ WATCHDOG: ${SPEECH_STOPPED_WATCHDOG_SILENCE_MS}ms of silence, no speech_stopped - closing turn`);
            this.voicedSilenceMs = 0;
            this.events.onSpeechStoppedTimeout?.();
          }
        }
      }
    }, this.SAFETY_CHECK_INTERVAL_MS);
  }
  
  /**
   * Stop safety check interval
   */
  private stopSafetyCheck(): void {
    if (this.safetyCheckInterval) {
      clearInterval(this.safetyCheckInterval);
      this.safetyCheckInterval = null;
    }
    this.voicedSilenceMs = 0;
  }
  
  /**
   * Soft reset: stop streaming but preserve pre-roll buffer.
   * Used by forceCloseGate() so that audio captured right before/during
   * a false-positive isn't lost – the pre-roll may contain the start of
   * real speech that began while noise was also present.
   */
  private softResetVADState(): void {
    logger.debug('[🔄 VAD-STATE] softResetVADState() - stop streaming, keep pre-roll');
    
    // Stop streaming to OpenAI
    this.vadWasOpen = false;
    this.lastGateState = false;
    this.gateOpenedTimestamp = null;
    this.receivedSpeechStarted = false;
    this.stopSafetyCheck();
    
    // Reset VAD gate so it can detect new speech
    if (this.vad) {
      this.vad.resetState();
    }
    
    // CRITICAL: Do NOT clear audioPreRollBuffer!
    // The buffer may contain the beginning of real speech that started
    // during or right after the noise event.
  }

  /**
   * Force close gate due to false positive.
   * Uses soft reset (preserves pre-roll buffer) and adds a cooldown
   * to prevent rapid gate flapping in noisy environments.
   */
  private forceCloseGate(): void {
    logger.debug('[🔄 VAD-STATE] Force closing gate - soft reset + cooldown');
    
    // Soft reset: stop streaming but keep pre-roll buffer intact
    this.softResetVADState();
    
    // Anti-flap cooldown: prevent the gate from reopening immediately
    // on the same noise burst that caused this false positive
    this.forceCloseCooldownUntil = Date.now() + this.FORCE_CLOSE_COOLDOWN_MS;
    
    // Notify to clear OpenAI buffer and UI
    this.events.onGateForceClosed?.();
  }

  /**
   * 🛡️ Check if there was recent audio activity (for speech_stopped validation)
   */
  public hasRecentAudioActivity(lookbackMs: number = 500): boolean {
    return this.vad?.hasRecentAudioActivity(lookbackMs) ?? false;
  }

  /**
   * Signal whether avatar audio is currently playing (forwarded to VAD).
   */
  public setPlaybackActive(active: boolean): void {
    this.vad?.setPlaybackActive(active);
    if (active) {
      this.lastPlaybackActiveTrueAt = Date.now();
    } else {
      this.lastPlaybackActiveFalseAt = Date.now();
    }
  }

  /**
   * Diagnostics snapshot for the on-screen audio diagnostics overlay.
   */
  public getRecordingDiagnostics() {
    const track = this.mediaStream?.getAudioTracks()[0];
    const settings = track?.getSettings?.();
    return {
      isRecording: this.isRecording,
      pttMode: this.pttMode,
      pttTransmitting: this.pttTransmitting,
      vadGateOpen: this.vad?.getState().gateOpen ?? false,
      vadVoicedMs: this.vad?.getState().voicedMs ?? 0,
      vadNoiseFloor: this.vad?.getState().noiseFloor ?? 0,
      lastPlaybackActiveTrueAt: this.lastPlaybackActiveTrueAt,
      lastPlaybackActiveFalseAt: this.lastPlaybackActiveFalseAt,
      micEchoCancellation: settings?.echoCancellation ?? null,
      micNoiseSuppression: settings?.noiseSuppression ?? null,
      micAutoGainControl: settings?.autoGainControl ?? null,
      micSampleRate: settings?.sampleRate ?? null,
      contextSampleRate: this.audioContext.sampleRate,
    };
  }

  /**
   * Register a polling callback so the VAD can check real-time playback state.
   */
  public setPlaybackChecker(fn: () => boolean): void {
    this.vad?.setPlaybackChecker(fn);
  }

  /**
   * Get current VAD state (forwarded from internal VAD instance).
   */
  public getVADState(): import('./VoiceActivityDetection').VADState | null {
    return this.vad?.getState() ?? null;
  }

  /**
   * Toggle atypical-voice VAD thresholds (for laryngectomy / esophageal speech).
   * Safe to call before VAD is created — value is stored and applied at setup.
   */
  public setAtypicalVoiceMode(enabled: boolean): void {
    this.atypicalVoiceMode = enabled;
    this.vad?.setAtypicalVoiceMode(enabled);
  }

  /**
   * Request microphone access and setup recording
   * Includes 48kHz resampling for OpenAI Realtime API
   */
  async setupRecording(): Promise<void> {
    try {
      logger.info('[🎤 SETUP] Starting microphone setup...');
      logger.debug('[🎤 SETUP] Config:', this.config);
      logger.debug('[🎤 SETUP] AudioContext state:', this.audioContext.state);
      logger.debug('[🎤 SETUP] AudioContext sampleRate:', this.audioContext.sampleRate);
      
      // Get microphone with native sample rate
      logger.debug('[🎤 SETUP] Requesting microphone access...');
      const rawStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: this.config.echoCancellation,
          noiseSuppression: this.config.noiseSuppression,
          autoGainControl: this.config.autoGainControl,
          channelCount: this.config.channelCount,
        }
      });
      
      logger.info('[🎤 SETUP] ✅ Microphone access granted');
      logger.debug('[🎤 SETUP] Stream tracks:', rawStream.getAudioTracks().length);
      const track = rawStream.getAudioTracks()[0];
      if (track) {
        const trackSettings = track.getSettings();
        logger.debug('[🎤 SETUP] Track settings:', trackSettings);
        logger.debug('[🎤 SETUP] Track constraints:', track.getConstraints());

        // 🔧 FIX (2026-08): Diagnostic logging for high-quality Bluetooth devices
        // (AirPods Pro et al.) that are vulnerable to the downsampling aliasing bug.
        // AirPods Pro report a 48kHz mic sample rate with strong high-frequency
        // content; the old linear-interpolation downsampler aliased that energy into
        // the speech band, producing "garbled input". This log helps confirm whether
        // a device is hitting the vulnerable 48kHz→24kHz downsample path.
        const deviceLabel = track.label || 'unknown';
        const isLikelyAirPods = /airpods/i.test(deviceLabel);
        const isLikelyBluetoothHFP = /airpods|headphones|bluetooth|buds/i.test(deviceLabel);
        const micRate = trackSettings?.sampleRate;
        const willDownsample = micRate && micRate > 24000;
        if (isLikelyAirPods || (isLikelyBluetoothHFP && willDownsample)) {
          logger.info(
            `[🎤 SETUP] 🎧 High-quality Bluetooth mic detected: "${deviceLabel}" ` +
            `(mic ${micRate}Hz → 24kHz ${willDownsample ? 'DOWNSAMPLE (anti-aliasing active)' : 'no downsample'})`
          );
        }
      }
      
      // 🔧 FIX: Determine the REAL input sample rate for resampling.
      //
      // AudioContext.sampleRate reflects the OUTPUT device (speakers/headphones),
      // NOT the microphone. When Bluetooth headphones activate their mic, the OS
      // switches from A2DP (44.1/48 kHz) to HFP (8/16 kHz). But the AudioContext
      // may have been created BEFORE this switch, so audioContext.sampleRate can
      // report 48000 while the mic is actually sending 16000 Hz audio.
      //
      // createMediaStreamSource() resamples the mic stream to match the
      // AudioContext.sampleRate, so the data flowing through the audio graph IS at
      // audioContext.sampleRate. This means the resampler should use
      // audioContext.sampleRate as its input — which is what we already do.
      //
      // HOWEVER: if the AudioContext was created before the Bluetooth profile switch,
      // audioContext.sampleRate is stale. The fix: detect the mic's actual sample rate
      // from MediaStreamTrack.getSettings(), and if it differs from
      // audioContext.sampleRate, recreate the AudioContext at the mic's rate to avoid
      // a double-resampling chain (mic→browser internal resample→our resample) that
      // degrades quality on cheap/Bluetooth devices.
      //
      // The safest approach: always use audioContext.sampleRate (since that IS what
      // flows through the graph), but LOG a warning when there's a mismatch so we
      // can detect these devices. If the mic reports a sample rate, we tell the
      // resampler the truth about what the AudioContext is actually running at.
      const micTrackSettings = track?.getSettings();
      const micReportedSampleRate = micTrackSettings?.sampleRate;
      const contextSampleRate = this.audioContext.sampleRate;
      
      // The input to our resampler is always at audioContext.sampleRate because
      // createMediaStreamSource() adapts the mic stream to the context's rate.
      // This is the correct value to pass to libsamplerate.
      let effectiveInputSampleRate = contextSampleRate;
      
      if (micReportedSampleRate && micReportedSampleRate !== contextSampleRate) {
        // 🚨 MISMATCH DETECTED: The microphone is natively running at a different
        // rate than the AudioContext. The browser will internally resample, but this
        // double-resampling (browser: mic→context, us: context→24kHz) can degrade
        // quality. Log prominently for diagnostics.
        console.warn(
          `[🎤 SETUP] ⚠️ SAMPLE RATE MISMATCH DETECTED!\n` +
          `  Microphone native rate: ${micReportedSampleRate} Hz\n` +
          `  AudioContext rate:      ${contextSampleRate} Hz\n` +
          `  This device may have poor transcription quality due to double-resampling.\n` +
          `  Attempting to create a new AudioContext at the microphone's native rate.`
        );
        
        // Try to create a new AudioContext at the mic's native sample rate.
        // This eliminates the browser's internal resampling step, giving
        // libsamplerate a clean signal to work with.
        //
        // 🧪 ORDER EXPERIMENT (2026-06): the mic is ALREADY open at this point, so on a
        // Bluetooth headset the device has already dropped into its low-rate "call" mode.
        // The explicit `{ sampleRate }` hint is ignored by Chrome (it hands back the
        // original 48 kHz), which is why this recreation has historically been a no-op on
        // Chrome. But a *hint-less* `new AudioContext()` opened while the mic is hot should
        // inherit the device's CURRENT output rate — which, in call mode, is the same low
        // rate as the mic. So if the hinted attempt is refused, retry with no hint and take
        // whatever rate Chrome gives, accepting it when it now matches the mic.
        try {
          let newContext = new AudioContext({ sampleRate: micReportedSampleRate });
          if (newContext.sampleRate !== micReportedSampleRate) {
            console.warn(`[🎤 SETUP] ⚠️ Browser ignored the ${micReportedSampleRate} Hz hint (got ${newContext.sampleRate}). Retrying hint-less now that the mic is open...`);
            try { await newContext.close(); } catch (_) { /* ignore */ }
            // Hint-less: let the browser pick the device's current default rate.
            newContext = new AudioContext();
            logger.info(`[🎤 SETUP] 🧪 Hint-less AudioContext opened at ${newContext.sampleRate} Hz (mic is ${micReportedSampleRate} Hz)`);
          }
          // Accept the new context if it now matches the mic's rate (no bridge needed).
          if (newContext.sampleRate === micReportedSampleRate) {
            // Suspend the old context and switch
            try { await this.audioContext.close(); } catch (_) { /* ignore */ }
            this.audioContext = newContext;
            effectiveInputSampleRate = micReportedSampleRate;
            logger.info(`[🎤 SETUP] ✅ AudioContext now matches mic's native rate: ${micReportedSampleRate} Hz (no resample bridge)`);
          } else {
            // Still mismatched — close the throwaway and keep the original context.
            console.warn(`[🎤 SETUP] ⚠️ Could not match mic rate ${micReportedSampleRate} Hz (context stuck at ${newContext.sampleRate} Hz). Keeping original context.`);
            try { await newContext.close(); } catch (_) { /* ignore */ }
          }
        } catch (ctxErr) {
          console.warn('[🎤 SETUP] ⚠️ Failed to create AudioContext at mic rate, keeping original:', ctxErr);
        }
      } else {
        logger.debug(`[🎤 SETUP] Sample rates match: mic=${micReportedSampleRate ?? 'unknown'}, context=${contextSampleRate}`);
      }
      
      logger.info(`[🎤 SETUP] Effective resampling: ${effectiveInputSampleRate} Hz → 24000 Hz (ratio: ${(effectiveInputSampleRate / 24000).toFixed(4)})`);
      
      // Create audio nodes (using potentially-new audioContext)
      const source = this.audioContext.createMediaStreamSource(rawStream);
      this.gainNode = this.audioContext.createGain();
      
      // Analysers for visualization
      this.analyserNode = this.audioContext.createAnalyser();
      this.analyserNode.fftSize = 512;
      this.analyserNode.smoothingTimeConstant = 0.3;
      this.analyserNode.minDecibels = -90;
      this.analyserNode.maxDecibels = -10;
      
      // Load resampling modules
      logger.debug('[🎤 SETUP] Loading AudioWorklet modules...');
      try {
        await this.audioContext.audioWorklet.addModule(
          'https://cdn.jsdelivr.net/npm/@alexanderolsen/libsamplerate-js/dist/libsamplerate.worklet.js'
        );
        logger.debug('[🎤 SETUP] ✅ Loaded libsamplerate module');
      } catch (err) {
        console.error('[🎤 SETUP] ❌ Failed to load libsamplerate:', err);
        const errMsg = err instanceof Error ? err.message : String(err);
        throw new Error(`Failed to load libsamplerate module: ${errMsg}`);
      }
      
      try {
        // Cache-busting: AudioWorklet.addModule() uses regular HTTP cache,
        // which is NOT cleared by hard refresh. Adding a version param ensures
        // the browser fetches a fresh copy after deployments.
        await this.audioContext.audioWorklet.addModule('/24khz-resampler.js?v=4');
        logger.debug('[🎤 SETUP] ✅ Loaded 24khz-resampler module (v4: init-gap + anti-alias fix)');
      } catch (err) {
        console.error('[🎤 SETUP] ❌ Failed to load 24khz-resampler:', err);
        const errMsg = err instanceof Error ? err.message : String(err);
        throw new Error(`Failed to load 24khz-resampler module: ${errMsg}`);
      }
      
      // Create resampler worklet (24kHz for OpenAI - MANDATORY!)
      // 🔧 FIX: Use effectiveInputSampleRate (from audioContext which may have been
      // recreated at mic's native rate) instead of blindly trusting the original context.
      logger.debug('[🎤 SETUP] Creating resampler AudioWorkletNode...');
      let resamplerNode;
      try {
        resamplerNode = new AudioWorkletNode(this.audioContext, '24khz-resampler', {
          processorOptions: {
            inputSampleRate: this.audioContext.sampleRate,
            outputSampleRate: 24000  // OpenAI requires 24kHz
          }
        });
        logger.debug(`[🎤 SETUP] ✅ Resampler node created (input: ${this.audioContext.sampleRate} Hz → output: 24000 Hz)`);
      } catch (err) {
        console.error('[🎤 SETUP] ❌ Failed to create resampler node:', err);
        const errMsg = err instanceof Error ? err.message : String(err);
        throw new Error(`Failed to create resampler AudioWorkletNode: ${errMsg}`);
      }
      
      const dest = this.audioContext.createMediaStreamDestination();
      const self = this;
      
      // 🔍 Audio Quality Pipeline: Counter and validation
      let audioChunksReceived = 0;
      let debugMessagesReceived = 0;
      
      // Listen to resampled audio data from worklet
      logger.debug('[🎤 SETUP] Registering port.onmessage handler...');
      resamplerNode.port.onmessage = (event) => {
        // Handle pong from worklet
        if (event.data.type === 'pong') {
          logger.debug('[🎤 SETUP] ✅ AudioWorklet active - using high-quality processing', {
            srcReady: event.data.srcReady,
            pendingSamples: event.data.pendingSamples
          });
          self.useAudioWorklet = true; // Confirm it works
          return;
        }

        // 🔧 FIX (2026-08): Handle 'ready' signal from worklet.
        // This fires when the libsamplerate WASM instance has finished initializing
        // and any audio buffered during init has been flushed. Distinct from 'pong'
        // (which only confirms the worklet thread is alive, not that resampling is
        // actually happening). Used to confirm the first-word-loss fix is working.
        if (event.data.type === 'ready') {
          logger.info(`[🎤 SETUP] ✅ Resampler READY (libsamplerate initialized, flushed ${event.data.flushedPendingSamples ?? 0} pending samples) — first-word capture active`);
          return;
        }

        // Handle debug messages from worklet (for logging only)
        if (event.data.type === 'debug') {
          debugMessagesReceived++;
          logger.debug(`[WORKLET ${debugMessagesReceived}] ${event.data.message}`, event.data);

          return;
        }
        
        // Handle audio data
        if (event.data.type === 'audio') {
          audioChunksReceived++;
          
          if (!self.isRecording) return;
          
          const resampledFloat32 = event.data.data;
          
          // ✅ PIPELINE STEP 2: Calculate amplitude for logging only
          // 🔧 FIX: Do NOT skip quiet chunks - they MUST go into pre-roll buffer
          // Otherwise we lose the start of soft speech and transcripts are incomplete
          let maxAmplitude = 0;
          for (let i = 0; i < resampledFloat32.length; i++) {
            maxAmplitude = Math.max(maxAmplitude, Math.abs(resampledFloat32[i]));
          }
          
          // ⚠️ CRITICAL: NEVER skip chunks before gate opens - they go to pre-roll buffer!
          // The pre-roll buffer needs ALL audio to capture the start of speech.
          // Only skip completely silent chunks (true digital silence, not soft speech)
          if (maxAmplitude < 0.0001 && !self.vadWasOpen) {
            // Truly silent (digital noise floor) AND gate never opened - safe to skip
            return;
          }
          
          // ✅ PIPELINE STEP 3: Validate size and buffer small chunks
          // ⚠️ PROBLEM: libsamplerate can return small chunks during resampling
          // Buffer small chunks until we have enough data for proper processing
          let processedFloat32: Float32Array = resampledFloat32;
          
          if (resampledFloat32.length < 100) {
            // Small chunks - buffer them until we have enough
            if (!self.smallChunkBuffer) {
              self.smallChunkBuffer = new Float32Array(0);
            }
            const combined = new Float32Array(self.smallChunkBuffer.length + resampledFloat32.length);
            combined.set(self.smallChunkBuffer);
            combined.set(resampledFloat32, self.smallChunkBuffer.length);
            self.smallChunkBuffer = combined;
            
            // Only process if buffer is large enough (unless gate is open - then send everything)
            if (self.smallChunkBuffer.length < 100 && !self.vadWasOpen) {
              return;
            }
            
            // Process accumulated buffer (even if small, if gate is open)
            processedFloat32 = self.smallChunkBuffer;
            self.smallChunkBuffer = new Float32Array(0);
          }
          
          // Convert Float32 to PCM16 (no processing - pass through directly)
          const pcm16 = new Int16Array(processedFloat32.length);
          for (let i = 0; i < processedFloat32.length; i++) {
            const s = Math.max(-1, Math.min(1, processedFloat32[i]));
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          
          // Convert to Uint8Array
          const pcm16Chunk = new Uint8Array(pcm16.buffer);
          
          // 🎙️ PTT mode: skip VAD gate logic, stream only when transmitting
          if (self.pttMode) {
            if (self.pttTransmitting) {
              self.events.onAudioData?.(pcm16Chunk);
              // Track peak RMS from the PCM16 data directly (no VAD dependency)
              const view = new Int16Array(pcm16Chunk.buffer, pcm16Chunk.byteOffset, pcm16Chunk.byteLength / 2);
              let sum = 0;
              for (let i = 0; i < view.length; i++) {
                const n = view[i] / 32768;
                sum += n * n;
              }
              const chunkRms = Math.sqrt(sum / view.length);
              if (chunkRms > self.pttPeakRms) {
                self.pttPeakRms = chunkRms;
              }
              // 🎙️ Fire onPTTSpeechDetected once when speech is first detected during PTT hold.
              // This allows the avatar to be interrupted as soon as the user starts speaking,
              // rather than waiting for the button to be released.
              if (!self.pttSpeechCallbackFired && self.pttPeakRms > self.PTT_SILENCE_RMS_THRESHOLD) {
                self.pttSpeechCallbackFired = true;
                self.events.onPTTSpeechDetected?.();
              }
            }
            // When not transmitting, discard audio (don't buffer)
            return;
          }
          
          // 🎯 VAD with pre-roll buffer logic
          if (self.vad) {
            const vadState = self.vad.getState();
            const gateIsOpen = vadState.gateOpen;
            
            // 🔄 Track gate transitions for detecting new speech segments
            const gateJustOpened = gateIsOpen && !self.lastGateState;
            self.lastGateState = gateIsOpen;
            
            if (!self.vadWasOpen) {
              // 🚪 WAITING FOR GATE TO OPEN (initial silence)
              
              // Check if gate just opened (do this BEFORE adding to buffer)
              // 🛡️ Anti-flap: skip gate open if we're in cooldown after a force close
              if (gateIsOpen && Date.now() >= self.forceCloseCooldownUntil) {
                // 🎯 GATE OPENED! Send pre-roll (BEFORE adding current chunk)
                const preRollToSend = [...self.audioPreRollBuffer];
                self.audioPreRollBuffer = [];
                
                // ✅ CRITICAL: Also send any buffered small chunks that might have been waiting
                if (self.smallChunkBuffer && self.smallChunkBuffer.length > 0) {
                  const bufferedPcm16 = new Int16Array(self.smallChunkBuffer.length);
                  for (let i = 0; i < self.smallChunkBuffer.length; i++) {
                    const s = Math.max(-1, Math.min(1, self.smallChunkBuffer[i]));
                    bufferedPcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                  }
                  preRollToSend.push(new Uint8Array(bufferedPcm16.buffer));
                  self.smallChunkBuffer = new Float32Array(0);
                }
                
                const now = Date.now();
                self.gateOpenedTimestamp = now;
                self.receivedSpeechStarted = false;
                
                logger.debug('[🔄 VAD-STATE] Gate opened - starting continuous stream + safety timers:', { 
                  preRollChunks: preRollToSend.length
                });
                
                // Set flags FIRST (before safety check depends on them)
                self.vadWasOpen = true;
                
                self.events.onSpeechDetected?.();
                self.startSafetyCheck();
                
                // Send pre-roll (does NOT include current chunk)
                for (const bufferedChunk of preRollToSend) {
                  self.events.onAudioData?.(bufferedChunk);
                }
                
                // Send current chunk (the one that opened the gate)
                self.events.onAudioData?.(pcm16Chunk);
              } else {
                // Gate still closed (or in cooldown) - buffer chunk for pre-roll
                // ✅ CRITICAL: Always buffer, even small chunks - they might be part of speech start
                self.audioPreRollBuffer.push(pcm16Chunk);
                if (self.audioPreRollBuffer.length > self.MAX_PREROLL_CHUNKS) {
                  self.audioPreRollBuffer.shift();
                }
              }
            } else {
              // ⚠️ CRITICAL: Already streaming - send EVERYTHING to OpenAI
              // OpenAI semantic_vad decides when speech ends (via speech_stopped event)
              
              // 🎯 NEW: Detect new speech segment via gate transition
              // This handles continued speech after prepareForNextSpeech() was called
              if (gateJustOpened) {
                logger.debug('[🔄 VAD-STATE] New speech segment detected while streaming');
                // Only start new timeout if we HAVEN'T received server confirmation yet
                // If server already confirmed speech (receivedSpeechStarted=true), don't reset!
                // Resetting would cause the safety timeout to fire and remove valid speech.
                if (!self.receivedSpeechStarted) {
                  self.gateOpenedTimestamp = Date.now();
                  self.startSafetyCheck();
                }
                self.events.onSpeechDetected?.(); // Still emit for visual feedback
              }
              
              self.events.onAudioData?.(pcm16Chunk);
            }
          } else {
            // No VAD - send everything
            self.events.onAudioData?.(pcm16Chunk);
          }
        }
      };
      
      // Connect: source → gain → analyser + resampler → dest
      source.connect(this.gainNode);
      this.gainNode.connect(this.analyserNode);
      this.gainNode.connect(resamplerNode);
      resamplerNode.connect(dest);
      
      // 🎯 CRITICAL: Initialize client-side VAD with pre-roll buffer
      // Client-side VAD detects the START of speech (decides when to begin sending audio chunks)
      // OpenAI semantic_vad detects the END of speech (decides when the user has finished speaking)
      // Pre-roll buffer captures audio BEFORE speech is detected to avoid cutting start of words
      this.vad = new VoiceActivityDetection(this.audioContext);
      this.vad.initialize(source, this.gainNode);
      this.vad.setOnSpeechLikely(() => this.events.onSpeechLikely?.());
      this.vad.setAtypicalVoiceMode(this.atypicalVoiceMode);
      logger.debug('[🎯 VAD] ✅ Client-side VAD initialized for speech START detection');
      
      // Set gain - no boost to avoid clipping
      this.setGain(1.0);
      
      this.mediaStream = rawStream;
      this.sourceNode = source;
      this.processedStream = dest.stream;
      
      // Store for cleanup
      this.resamplerNode = resamplerNode;
      
      logger.info('[🎤 SETUP] ✅ Setup complete - ready to record');
      
      // Test if AudioWorklet is actually working (known issue on mobile Safari/Firefox)
      // ✅ Check if we're on Firefox mobile - AudioWorklet may exist but not work properly
      const isFirefoxMobile = /Firefox/i.test(navigator.userAgent) && /Mobile/i.test(navigator.userAgent);
      
      if (isFirefoxMobile) {
        logger.debug('[🎤 SETUP] ⚠️ Firefox mobile detected - AudioWorklet may not work properly, testing...');
      }
      
      setTimeout(() => {
        logger.debug('[🎤 SETUP] Testing AudioWorklet responsiveness...');
        let pongReceived = false;
        
        // Set up listener for pong BEFORE sending ping
        const pongListener = (event: MessageEvent) => {
          if (event.data.type === 'pong') {
            pongReceived = true;
            logger.debug('[🎤 SETUP] ✅ AudioWorklet responded to ping:', event.data);
            resamplerNode.port.removeEventListener('message', pongListener);
          }
        };
        resamplerNode.port.addEventListener('message', pongListener);
        
        try {
          resamplerNode.port.postMessage({ type: 'ping' });
          
          // If no pong after 2 seconds, switch to fallback (increased timeout for Firefox)
          setTimeout(() => {
            if (!pongReceived && self.useAudioWorklet) {
              console.warn('[🎤 SETUP] ⚠️ AudioWorklet not responding - using ScriptProcessor fallback');
              resamplerNode.port.removeEventListener('message', pongListener);
              self.switchToScriptProcessorFallback(source);
            }
          }, 2000); // Increased timeout for Firefox mobile
        } catch (err) {
          console.error('[🎤 SETUP] ❌ Failed to test worklet:', err);
          resamplerNode.port.removeEventListener('message', pongListener);
          self.switchToScriptProcessorFallback(source);
        }
      }, 100);

    } catch (error) {
      console.error('[🎤 SETUP] ❌ ERROR during setup:', error);
      console.error('[🎤 SETUP] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Log microphone errors to session tracking
      const errorObj = error as Error;
      let errorType = 'microphone_error';
      
      if (errorObj.name === 'NotAllowedError') {
        errorType = 'microphone_permission_denied';
      } else if (errorObj.name === 'NotFoundError') {
        errorType = 'microphone_not_found';
      } else if (errorObj.name === 'NotReadableError') {
        errorType = 'microphone_in_use';
      }
      
      // Note: Cannot log to sessionTracking here - no sessionId available in audio layer
      // Microphone errors are reported via events.onError callback instead
      console.error(`[AudioRecording] ${errorType}:`, errorObj.message);
      
      this.events.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Start audio recording
   */
  startRecording(): void {
    logger.debug('[🎤 RECORDING] Starting recording');
    
    if (!this.mediaStream || this.isRecording) {
      logger.debug('[🎤 RECORDING] ❌ Skipping - already recording or no stream');
      return;
    }

    try {
      // Enable mic tracks (may be disabled from pauseMicrophone)
      this.mediaStream.getAudioTracks().forEach(track => {
        track.enabled = true;
      });
      
      logger.debug('[🎤 RECORDING] ✅ Recording active');
      
      this.isRecording = true;
      
      // 🎯 Start VAD monitoring
      if (this.vad) {
        this.vad.startMonitoring();
        logger.debug('[🎯 VAD] Started monitoring for speech detection');
      }
      
      this.startVolumeMonitoring();
      this.events.onRecordingStart?.();

    } catch (error) {
      console.error('[🎤 WAVEFORM] ❌ Error starting recording:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * 📱 MOBILE SAFARI FALLBACK: Switch to ScriptProcessorNode
   * 
   * AudioWorklet doesn't work reliably on mobile Safari (especially iOS).
   * This fallback uses deprecated ScriptProcessorNode but is necessary for mobile compatibility.
   * 
   * CRITICAL: This code must be kept in sync with AudioWorklet VAD logic!
   * Both paths MUST:
   * - Use vadWasOpen to track if gate ever opened
   * - Send chunks continuously after first gate open (for semantic_vad)
   * - Only buffer pre-roll when gate is CLOSED
   */
  private switchToScriptProcessorFallback(source: MediaStreamAudioSourceNode): void {
    logger.info('[🎤 FALLBACK] Switching to ScriptProcessorNode (mobile Safari compatibility)...');
    this.useAudioWorklet = false;
    
    try {
      // Disconnect AudioWorklet if it was connected
      if (this.resamplerNode) {
        try {
          this.resamplerNode.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
      }
      
      // Create ScriptProcessorNode (4096 buffer, mono in/out)
      const bufferSize = 4096;
      this.audioProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
      
      const self = this;
      const targetSampleRate = 24000;  // OpenAI requires 24kHz
      const sourceSampleRate = this.audioContext.sampleRate;
      
      // ✅ Try to initialize libsamplerate for high-quality resampling (using "in modules" version)
      // This works in main thread unlike the AudioWorklet version
      // Note: This is async - will use linear interpolation until libsamplerate is ready
      this.initLibsamplerateForScriptProcessor(sourceSampleRate, targetSampleRate)
        .then(() => {
          logger.debug('[🎤 FALLBACK] ✅ libsamplerate ready for ScriptProcessor');
        })
        .catch(err => {
          console.warn('[🎤 FALLBACK] ⚠️ Failed to initialize libsamplerate for ScriptProcessor, using linear interpolation:', err);
        });
      
      const sampleRateRatio = targetSampleRate / sourceSampleRate;
      let fallbackChunkCount = 0;
      
      this.audioProcessor.onaudioprocess = (e) => {
        if (!self.isRecording) return;
        
        fallbackChunkCount++;
        
        const inputData = e.inputBuffer.getChannelData(0);
        const inputLength = inputData.length;
        
        // ✅ PIPELINE STEP 2: Calculate amplitude for logging only
        // 🔧 FIX: Do NOT skip quiet chunks - they MUST go into pre-roll buffer
        let maxAmplitude = 0;
        for (let i = 0; i < inputLength; i++) {
          maxAmplitude = Math.max(maxAmplitude, Math.abs(inputData[i]));
        }
        
        // ⚠️ CRITICAL: NEVER skip chunks before gate opens - they go to pre-roll buffer!
        // Only skip completely silent chunks (true digital silence, not soft speech)
        if (maxAmplitude < 0.0001 && !self.vadWasOpen) {
          // Truly silent (digital noise floor) AND gate never opened - safe to skip
          return;
        }
        
        // ✅ Use libsamplerate if available, otherwise fall back to linear interpolation
        // 🔧 FIX (2026-08): When DOWNSAMPLING (sourceSampleRate > targetSampleRate),
        // linear interpolation alone causes severe aliasing — frequencies above the
        // new Nyquist (targetSampleRate/2) fold back into the speech band (300-3400Hz),
        // producing the "garbled input" reported by AirPods Pro users. AirPods Pro
        // have strong high-frequency content (up to 20kHz+), so they alias the worst;
        // cheap laptop mics and old Bluetooth HFP headsets (8/16kHz) have almost no
        // high-frequency energy, so the bug is inaudible on them.
        // FIX: apply a low-pass filter BEFORE decimating. libsamplerate's
        // SRC_SINC_BEST_QUALITY already does this internally, so the filter is only
        // needed in the linear-interpolation fallback paths below.
        let resampled: Float32Array;
        
        if (self.libsamplerateFallback) {
          try {
            // Use libsamplerate for high-quality resampling (same quality as AudioWorklet!)
            // SRC_SINC_BEST_QUALITY applies its own brick-wall low-pass filter.
            resampled = self.libsamplerateFallback.full(inputData);
          } catch (err) {
            console.warn('[🎤 FALLBACK] ⚠️ libsamplerate error during resampling, falling back to linear:', err);
            // Fall back to linear interpolation WITH anti-aliasing low-pass filter
            resampled = self.downsampleWithAntiAliasing(inputData, sampleRateRatio);
          }
        } else {
          // Linear interpolation fallback (libsamplerate not ready yet or failed)
          // 🔧 FIX: apply low-pass filter first to prevent aliasing when downsampling.
          resampled = self.downsampleWithAntiAliasing(inputData, sampleRateRatio);
        }
        
        // ✅ PIPELINE STEP 3: Validate size and buffer small chunks
        if (resampled.length < 100) {
          // Small chunks - buffer them until we have enough
          if (!self.smallChunkBuffer) {
            self.smallChunkBuffer = new Float32Array(0);
          }
          const combined = new Float32Array(self.smallChunkBuffer.length + resampled.length);
          combined.set(self.smallChunkBuffer);
          combined.set(resampled, self.smallChunkBuffer.length);
          self.smallChunkBuffer = combined;
          
          // Only process if buffer is large enough (unless gate is open - then send everything)
          if (self.smallChunkBuffer.length < 100 && !self.vadWasOpen) {
            return;
          }
          
          // Process accumulated buffer (even if small, if gate is open)
          resampled = self.smallChunkBuffer;
          self.smallChunkBuffer = new Float32Array(0);
        }
        
        // Convert Float32 to PCM16 (no processing - pass through directly)
        const pcm16 = new Int16Array(resampled.length);
        for (let i = 0; i < resampled.length; i++) {
          const s = Math.max(-1, Math.min(1, resampled[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        
        // Convert to Uint8Array
        const pcm16Chunk = new Uint8Array(pcm16.buffer);
        
        // 🎙️ PTT mode: skip VAD gate logic, stream only when transmitting
        if (self.pttMode) {
          if (self.pttTransmitting) {
            self.events.onAudioData?.(pcm16Chunk);
            // Track peak RMS from the PCM16 data directly (no VAD dependency)
            const view = new Int16Array(pcm16Chunk.buffer, pcm16Chunk.byteOffset, pcm16Chunk.byteLength / 2);
            let sum = 0;
            for (let i = 0; i < view.length; i++) {
              const n = view[i] / 32768;
              sum += n * n;
            }
            const chunkRms = Math.sqrt(sum / view.length);
            if (chunkRms > self.pttPeakRms) {
              self.pttPeakRms = chunkRms;
            }
            // 🎙️ Fire onPTTSpeechDetected once when speech is first detected during PTT hold.
            // This allows the avatar to be interrupted as soon as the user starts speaking,
            // rather than waiting for the button to be released.
            if (!self.pttSpeechCallbackFired && self.pttPeakRms > self.PTT_SILENCE_RMS_THRESHOLD) {
              self.pttSpeechCallbackFired = true;
              self.events.onPTTSpeechDetected?.();
            }
          }
          return;
        }
        
        // 🎯 VAD with pre-roll buffer logic
        // 📱 CRITICAL: This MUST match AudioWorklet implementation
        // Both paths must behave identically for consistent experience desktop/mobile
        if (self.vad) {
          const vadState = self.vad.getState();
          const gateIsOpen = vadState.gateOpen;
          
          // 🔄 Track gate transitions for detecting new speech segments
          const gateJustOpened = gateIsOpen && !self.lastGateState;
          self.lastGateState = gateIsOpen;
          
          if (!self.vadWasOpen) {
            // 🚪 WAITING FOR GATE TO OPEN (initial silence)
            
            // Check if gate just opened (do this BEFORE adding to buffer)
            // 🛡️ Anti-flap: skip gate open if we're in cooldown after a force close
            if (gateIsOpen && Date.now() >= self.forceCloseCooldownUntil) {
              // 🎯 GATE OPENED! Send pre-roll (BEFORE adding current chunk)
              const preRollToSend = [...self.audioPreRollBuffer];
              self.audioPreRollBuffer = [];
              
              // ✅ CRITICAL: Also send any buffered small chunks that might have been waiting
              if (self.smallChunkBuffer && self.smallChunkBuffer.length > 0) {
                const bufferedPcm16 = new Int16Array(self.smallChunkBuffer.length);
                for (let i = 0; i < self.smallChunkBuffer.length; i++) {
                  const s = Math.max(-1, Math.min(1, self.smallChunkBuffer[i]));
                  bufferedPcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }
                preRollToSend.push(new Uint8Array(bufferedPcm16.buffer));
                self.smallChunkBuffer = new Float32Array(0);
              }
              
              const now = Date.now();
              self.gateOpenedTimestamp = now;
              self.receivedSpeechStarted = false;
              
              logger.debug('[🔄 VAD-STATE] 📱 Gate opened - starting continuous stream + safety timers:', {
                preRollChunks: preRollToSend.length
              });
              
              // Set flags FIRST (before safety check depends on them)
              self.vadWasOpen = true;
              
              self.events.onSpeechDetected?.();
              self.startSafetyCheck();
              
              // Send pre-roll (does NOT include current chunk)
              for (const bufferedChunk of preRollToSend) {
                self.events.onAudioData?.(bufferedChunk);
              }
              
              // Send current chunk (the one that opened the gate)
              self.events.onAudioData?.(pcm16Chunk);
            } else {
              // Gate still closed (or in cooldown) - buffer chunk for pre-roll
              // ✅ CRITICAL: Always buffer, even small chunks - they might be part of speech start
              self.audioPreRollBuffer.push(pcm16Chunk);
              if (self.audioPreRollBuffer.length > self.MAX_PREROLL_CHUNKS) {
                self.audioPreRollBuffer.shift();
              }
            }
          } else {
            // ⚠️ CRITICAL: Already streaming - send EVERYTHING to OpenAI
            // OpenAI semantic_vad decides when speech ends (via speech_stopped event)
            
            // 🎯 NEW: Detect new speech segment via gate transition
            // This handles continued speech after prepareForNextSpeech() was called
            if (gateJustOpened) {
              logger.debug('[🔄 VAD-STATE] 📱 New speech segment detected while streaming');
              // Only start new timeout if we HAVEN'T received server confirmation yet
              // If server already confirmed speech (receivedSpeechStarted=true), don't reset!
              // Resetting would cause the safety timeout to fire and remove valid speech.
              if (!self.receivedSpeechStarted) {
                self.gateOpenedTimestamp = Date.now();
                self.startSafetyCheck();
              }
              self.events.onSpeechDetected?.(); // Still emit for visual feedback
            }
            
            self.events.onAudioData?.(pcm16Chunk);
          }
        } else {
          self.events.onAudioData?.(pcm16Chunk);
        }
      };
      
      // Reconnect: source → gain → analyser + processor
      if (this.gainNode) {
        source.connect(this.gainNode);
        this.gainNode.connect(this.analyserNode!);
        this.gainNode.connect(this.audioProcessor);
        this.audioProcessor.connect(this.audioContext.destination);
      }
      
      logger.debug('[🎤 FALLBACK] ✅ ScriptProcessorNode active (mobile compatibility mode)');
      
    } catch (error) {
      console.error('[🎤 FALLBACK] ❌ Failed to switch to fallback:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Stop audio recording
   */
  stopRecording(): void {
    if (!this.isRecording) {
      return;
    }

    try {
      this.isRecording = false;
      
      // 🎯 Stop VAD monitoring
      if (this.vad) {
        this.vad.stopMonitoring();
        this.vadWasOpen = false;
        this.audioPreRollBuffer = [];
        logger.debug('[🎯 VAD] Stopped monitoring');
      }

      this.stopSafetyCheck();
      this.receivedSpeechStarted = false;

      // Reset audio smoothing state
      this.smallChunkBuffer = null;

      this.stopVolumeMonitoring();
      this.events.onRecordingStop?.();

    } catch (error) {
      console.error('Error stopping recording:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Pause microphone input (disable audio tracks temporarily)
   */
  pauseMicrophone(): void {
    if (!this.mediaStream) {
      console.warn('[🎤 PAUSE] ⚠️ No media stream to pause');
      return;
    }

    try {
      const audioTracks = this.mediaStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = false;
      });
      
      // Update recording state
      if (this.isRecording) {
        this.isRecording = false;

        // 🎯 Stop VAD when pausing
        if (this.vad) {
          this.vad.stopMonitoring();
          this.vadWasOpen = false;
          this.audioPreRollBuffer = [];

          // Reset audio smoothing state
          this.smallChunkBuffer = null;
        }

        this.stopSafetyCheck();
        this.receivedSpeechStarted = false;

        this.events.onRecordingStop?.();
      }
      
      logger.debug('[🎤 PAUSE] 🛑 Microphone paused');
    } catch (error) {
      console.error('[🎤 PAUSE] ❌ Error pausing microphone:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Resume microphone input (re-enable audio tracks)
   */
  resumeMicrophone(): void {
    if (!this.mediaStream) {
      console.warn('[🎤 PAUSE] ⚠️ No media stream to resume');
      return;
    }

    try {
      const audioTracks = this.mediaStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = true;
      });
      
      // Update recording state
      if (!this.isRecording) {
        this.isRecording = true;
        
        // 🎯 Restart VAD when resuming
        if (this.vad) {
          this.vad.startMonitoring();
          logger.debug('[🎯 VAD] Restarted monitoring');
        }
        
        this.startVolumeMonitoring();
        this.events.onRecordingStart?.();
      }
      
      logger.debug('[🎤 PAUSE] ▶️ Microphone resumed');
    } catch (error) {
      console.error('[🎤 PAUSE] ❌ Error resuming microphone:', error);
      this.events.onError?.(error as Error);
    }
  }

  // =========================================================================
  // PTT (Push-to-Talk) Mode
  // =========================================================================

  /**
   * Enable/disable PTT mode.
   * In PTT mode, VAD is disabled and audio streaming is controlled manually
   * via startPTTTransmit() / stopPTTTransmit().
   */
  setPTTMode(enabled: boolean): void {
    if (this.pttMode === enabled) return;
    
    this.pttMode = enabled;
    this.pttTransmitting = false;
    
    if (enabled) {
      // Disable VAD - PTT controls audio flow
      if (this.vad) {
        this.vad.stopMonitoring();
      }
      this.resetAllVADState();
      logger.debug('[🎙️ PTT] PTT mode enabled - VAD disabled');
    } else {
      // Re-enable VAD
      if (this.vad && this.isRecording) {
        this.vad.startMonitoring();
      }
      logger.debug('[🎙️ PTT] PTT mode disabled - VAD re-enabled');
    }
  }

  /**
   * Check if PTT mode is active
   */
  isPTTModeActive(): boolean {
    return this.pttMode;
  }

  /**
   * Check if PTT is currently transmitting
   */
  isPTTTransmitting(): boolean {
    return this.pttMode && this.pttTransmitting;
  }

  /**
   * Start PTT transmission (user pressed the button).
   * Audio chunks will now stream to the WebSocket.
   * Also starts VAD monitoring to detect if user actually speaks.
   */
  startPTTTransmit(): void {
    if (!this.pttMode) {
      logger.warn('[🎙️ PTT] Cannot start transmit - not in PTT mode');
      return;
    }
    if (!this.isRecording) {
      logger.warn('[🎙️ PTT] Cannot start transmit - not recording');
      return;
    }
    
    this.pttTransmitting = true;
    this.pttPeakRms = 0; // reset peak RMS tracker for this PTT session
    this.pttSpeechCallbackFired = false; // reset so onPTTSpeechDetected fires for this new PTT session
    // Set vadWasOpen so audio chunks stream through (same flag used by the pipeline)
    this.vadWasOpen = true;
    this.audioPreRollBuffer = [];
    
    // NOTE: We do NOT start VAD monitoring during PTT anymore.
    // Speech detection is now based on peak RMS of actual PCM16 chunks sent,
    // measured directly in the audio processing loop. This is simpler and more
    // reliable than the full VAD gate logic which depends on AnalyserNode timing.
    
    logger.debug('[🎙️ PTT] Transmitting started (RMS-based silence detection)');
  }

  /**
   * Stop PTT transmission (user released the button).
   * Stops VAD monitoring and returns whether speech was detected.
   * Caller should only commit the buffer if speech was detected.
   * 
   * @returns true if the VAD detected speech during this PTT session
   */
  stopPTTTransmit(): boolean {
    if (!this.pttMode) return false;
    
    // Speech detection based on peak RMS of actual PCM16 audio chunks sent.
    // This is measured directly in the audio processing loop — no dependency
    // on the VAD AnalyserNode or its monitoring interval.
    const speechDetected = this.pttPeakRms > this.PTT_SILENCE_RMS_THRESHOLD;
    
    this.pttTransmitting = false;
    this.vadWasOpen = false;
    this.audioPreRollBuffer = [];
    
    logger.debug(`[🎙️ PTT] Transmitting stopped (peakRms: ${this.pttPeakRms.toFixed(4)}, threshold: ${this.PTT_SILENCE_RMS_THRESHOLD}, speechDetected: ${speechDetected})`);
    this.pttPeakRms = 0;
    return speechDetected;
  }

  /**
   * Get waveform data for microphone input visualization
   * Returns array of 9 values (0-255) representing audio amplitude
   */
  getRecordingFrequencyData(): Uint8Array {
    if (!this.analyserNode || !this.isRecording) {
      return new Uint8Array(9).fill(0);
    }
    
    const result = this.getFrequencyDataFromAnalyser(this.analyserNode);
    
    return result;
  }

  /**
   * Internal helper to get frequency data from analyser node
   */
  private getFrequencyDataFromAnalyser(analyser: AnalyserNode): Uint8Array {
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    
    // Get time domain data (waveform amplitude)
    analyser.getByteTimeDomainData(dataArray);
    
    // Group into 9 bars
    const barCount = 9;
    const barsData = new Uint8Array(barCount);
    const samplesPerBar = Math.floor(bufferLength / barCount);
    
    for (let i = 0; i < barCount; i++) {
      let maxValue = 0;
      const start = i * samplesPerBar;
      const end = start + samplesPerBar;
      
      for (let j = start; j < end && j < bufferLength; j++) {
        const amplitude = Math.abs(dataArray[j] - 128);
        maxValue = Math.max(maxValue, amplitude);
      }
      
      // Light amplification
      const amplified = maxValue * 1.5;
      barsData[i] = Math.min(255, amplified);
    }
    
    return barsData;
  }

  /**
   * Set gain level
   */
  setGain(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.config.volume = clampedVolume;

    if (this.gainNode) {
      this.gainNode.gain.value = clampedVolume;
    }
  }

  /**
   * Get current gain level
   */
  getGain(): number {
    return this.config.volume ?? 1.0;
  }

  /**
   * Get media stream for WebRTC
   * Returns processed stream (48kHz) instead of raw mic stream
   */
  getMediaStream(): MediaStream | null {
    return this.processedStream || this.mediaStream;
  }

  /**
   * Start monitoring audio levels
   */
  private startVolumeMonitoring(): void {
    if (this.volumeCheckInterval || !this.analyserNode) {
      return;
    }

    const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

    this.volumeCheckInterval = setInterval(() => {
      if (!this.analyserNode || !this.isRecording) {
        return;
      }

      this.analyserNode.getByteFrequencyData(dataArray);

      // Calculate RMS level
      let sum = 0;
      let peak = 0;
      
      for (let i = 0; i < dataArray.length; i++) {
        const value = dataArray[i] / 255.0;
        sum += value * value;
        peak = Math.max(peak, value);
      }

      const rms = Math.sqrt(sum / dataArray.length);
      
      this.events.onAudioLevel?.({
        level: rms,
        peak: peak,
        timestamp: Date.now(),
      });

    }, 100); // Check every 100ms
  }

  /**
   * Stop monitoring audio levels
   */
  private stopVolumeMonitoring(): void {
    if (this.volumeCheckInterval) {
      clearInterval(this.volumeCheckInterval);
      this.volumeCheckInterval = null;
    }
  }

  /**
   * 🔧 FIX (2026-08): Downsample with anti-aliasing low-pass filter.
   *
   * PROBLEM: The old linear-interpolation downsampler decimated without any
   * low-pass filtering. When sourceSampleRate > targetSampleRate (e.g. 48kHz→24kHz),
   * frequencies above the new Nyquist (12kHz) alias back into the 0-12kHz band,
   * corrupting the speech region (300-3400Hz). This produced "garbled input"
   * specifically on high-quality microphones like AirPods Pro, which have strong
   * high-frequency content (up to 20kHz+). Cheap mics and old Bluetooth HFP
   * headsets (8/16kHz) have almost no energy above 12kHz, so the aliasing was
   * inaudible — which is why only AirPods Pro users reported the problem.
   *
   * FIX: Apply a one-pole IIR low-pass filter (cutoff ≈ 0.8 × new Nyquist) before
   * linear-interpolation decimation. This is not as clean as libsamplerate's
   * SRC_SINC_BEST_QUALITY brick-wall filter, but it eliminates the vast majority
   * of aliasing and is cheap enough for the main-thread ScriptProcessor path.
   * The primary path (AudioWorklet + libsamplerate) is unaffected.
   *
   * When UPSAMPLING (sourceSampleRate < targetSampleRate), no filter is needed —
   * linear interpolation is safe because there are no frequencies to alias.
   *
   * @param inputData  Float32Array of input samples at sourceSampleRate
   * @param ratio       targetSampleRate / sourceSampleRate (e.g. 24000/48000 = 0.5)
   * @returns           Float32Array resampled to targetSampleRate
   */
  private downsampleWithAntiAliasing(inputData: Float32Array, ratio: number): Float32Array {
    const inputLength = inputData.length;
    const outputLength = Math.floor(inputLength * ratio);
    const resampled = new Float32Array(outputLength);

    // Only filter when downsampling (ratio < 1). Upsampling is alias-free.
    if (ratio >= 1.0) {
      // Upsample: plain linear interpolation (safe — no aliasing risk)
      for (let i = 0; i < outputLength; i++) {
        const srcIndex = i / ratio;
        const srcIndexFloor = Math.floor(srcIndex);
        const srcIndexCeil = Math.min(srcIndexFloor + 1, inputLength - 1);
        const t = srcIndex - srcIndexFloor;
        resampled[i] = inputData[srcIndexFloor] * (1 - t) + inputData[srcIndexCeil] * t;
      }
      return resampled;
    }

    // Downsample: one-pole IIR low-pass filter, then linear-interpolation decimate.
    // Cutoff at ~0.8 × (targetSampleRate/2) to leave a guard band above the new
    // Nyquist. For 48kHz→24kHz, cutoff ≈ 0.8 × 12000 = 9600 Hz.
    // alpha = cutoff / (cutoff + RC), derived from the standard one-pole RC filter.
    // We compute alpha directly from the ratio so it adapts to any rate pair.
    const cutoffFraction = 0.8;   // fraction of new Nyquist
    const normalizedCutoff = cutoffFraction * ratio; // cutoff as fraction of source Nyquist
    // alpha for one-pole IIR: alpha = 2π·fc/fs ≈ normalizedCutoff·π (small-angle approx)
    // Clamp to (0,1) for safety.
    const alpha = Math.max(0.001, Math.min(0.999, Math.PI * normalizedCutoff));

    // In-place low-pass filter (one-pole IIR). We filter into a copy to preserve
    // the input buffer (ScriptProcessor recycles it, but other consumers like the
    // analyser may read it — safer not to mutate).
    const filtered = new Float32Array(inputLength);
    let prev = inputData[0];
    filtered[0] = prev;
    for (let i = 1; i < inputLength; i++) {
      prev = prev + alpha * (inputData[i] - prev);
      filtered[i] = prev;
    }

    // Linear-interpolation decimation on the filtered signal.
    for (let i = 0; i < outputLength; i++) {
      const srcIndex = i / ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, inputLength - 1);
      const t = srcIndex - srcIndexFloor;
      resampled[i] = filtered[srcIndexFloor] * (1 - t) + filtered[srcIndexCeil] * t;
    }

    return resampled;
  }

  /**
   * Initialize libsamplerate for ScriptProcessor fallback using "in modules" version
   * This allows high-quality resampling in main thread (unlike AudioWorklet version)
   * ✅ Uses dynamic import to avoid bundling libsamplerate in main bundle (only loads when fallback is used)
   */
  private async initLibsamplerateForScriptProcessor(inputSampleRate: number, outputSampleRate: number): Promise<void> {
    try {
      logger.debug('[🎤 FALLBACK] Initializing libsamplerate for ScriptProcessor fallback...');
      
      // ✅ Dynamic import - only loads when ScriptProcessor fallback is used (not in AudioWorklet path)
      const libsamplerate = await import('@alexanderolsen/libsamplerate-js');
      const { create, ConverterType } = libsamplerate;
      
      // Create resampler instance using "in modules" API (same as AudioWorklet quality!)
      this.libsamplerateFallback = await create(1, inputSampleRate, outputSampleRate, {
        converterType: ConverterType.SRC_SINC_BEST_QUALITY,
      });
      
      logger.debug('[🎤 FALLBACK] ✅ libsamplerate initialized for ScriptProcessor fallback');
      logger.debug('[🎤 FALLBACK] Channels: 1 (mono), Input:', inputSampleRate, 'Output:', outputSampleRate);
    } catch (err) {
      console.error('[🎤 FALLBACK] ❌ Failed to initialize libsamplerate:', err);
      this.libsamplerateFallback = null;
      throw err;
    }
  }

  /**
   * Clean up all recording resources
   */
  cleanup(): void {
    this.stopRecording();
    this.stopVolumeMonitoring();

    // Disconnect audio nodes
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }

    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }

    if (this.audioProcessor) {
      this.audioProcessor.disconnect();
      this.audioProcessor.onaudioprocess = null;
      this.audioProcessor = null;
    }

    // Stop media streams
    if (this.processedStream) {
      this.processedStream.getTracks().forEach(track => track.stop());
      this.processedStream = null;
    }
    
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }
}

